/**
 * GroupCard — one position group's card, shared by BOTH /rate reports.
 *
 * 🔴 EXTRACTED, NOT FORKED (round 6, Aug 30 2026). Ben, about the league
 * report's expanded rosters: "use the same format as in the rate my team." The
 * card, its column grid, its header row and its dash rule lived inside
 * TeamReportView; copying them onto the league page would have produced two
 * cards that look identical on the day they ship and drift apart on every day
 * after. So the whole thing moved here and both pages import it.
 *
 * WHAT THIS COMPONENT KNOWS: how to draw a badge, a coloured position title, a
 * note, a header and a set of player rows. WHAT IT DOES NOT KNOW: what the
 * badge means. That is the one real difference between the two pages and it is
 * a prop rather than a branch:
 *   TEAM TAB    badge = a corpus letter grade, sub = the percentile.
 *   LEAGUE PAGE badge = the room rank word (strong/solid/thin), no number,
 *               because that page ranks a team against its own eleven
 *               neighbours and a corpus percentile there would be a second
 *               yardstick on a page that already has one.
 * A component that decided this itself would need to know which page it was
 * on, which is exactly the branch this split exists to avoid.
 *
 * The rows are the same everywhere: ppg / season / rank, then paid / market /
 * +/- when the page has prices and the viewport is wide enough to carry them.
 * `rowInfo` supplies the two per-player numbers the card cannot derive - the
 * projection and the position rank - because the two pages hold them in
 * different objects.
 */

import { useState } from "react";
import { POS_COLORS } from "../../data/players.js";
import { theme } from "../../data/theme.js";
import PlayerAvatar from "../PlayerAvatar.jsx";
import { C, SZ, card } from "../Submit/submitStyles.js";
import { money, signedMoney } from "../../utils/money.js";
import { avatarPlayer } from "./avatarPlayer.js";
import { expandEnter, prefersReducedMotion } from "./rateMotion.js";

/**
 * Re-exported, not defined here. The formatter moved to utils/money.js when
 * roomSource.js needed it — see that file's header — and every card on this page
 * still reaches it by the name it always used, so there is one definition and no
 * import churn.
 */
export { money, signedMoney };

/**
 * The "we have no number for this" mark. An en dash, muted, never an empty cell.
 *
 * 🔴 AN EMPTY CELL IS WHAT MADE PITTMAN READ AS "GOING FOR $1". His market and
 * surplus were blank because a <=$3 sale sits outside the market table, so the
 * row rendered a lone unlabelled "$1" with nothing beside it. A dash says the
 * column was considered and has no value; a blank says nothing at all, and the
 * reader fills it in themselves.
 */
export const NONE = "–";

/** One cell of the numeric grid, so a header and a row cannot drift apart. */
function Cell({ col, children, color, weight }) {
  return (
    <span
      style={{
        width: col.width,
        flexShrink: 0,
        textAlign: "right",
        fontSize: SZ.small,
        fontWeight: weight || 400,
        color: color || C.text.secondary,
      }}
    >
      {children}
    </span>
  );
}

/**
 * The numeric columns on a group card, in order, defined ONCE.
 *
 * Ben: "Is he going for $1? I really want to see projected PPG numbers near the
 * player." So PPG leads: it is the number the Projection Lab speaks and the one
 * a person can hold in their head against a start/sit call. The season total
 * stays, one step quieter, because it is what the positional scores are built
 * from and dropping it would make the badges unauditable.
 *
 * The three price columns collapse together on a phone, behind the same tap
 * that already hid them, and the header collapses with them: a header row that
 * names columns the rows are not drawing is worse than no header.
 */
export const COLS = {
  ppg: { label: "ppg", width: 44 },
  season: { label: "season", width: 52 },
  rank: { label: "rank", width: 46 },
  paid: { label: "paid", width: 44 },
  market: { label: "market", width: 50 },
  delta: { label: "+/-", width: 50 },
};

const PRICE_COLS = ["paid", "market", "delta"];

/** The muted header row. `priceCols` must be the same flag the rows are given. */
export function GroupHeader({ priceCols }) {
  const keys = ["ppg", "season", "rank", ...(priceCols ? PRICE_COLS : [])];
  return (
    <div
      data-testid="group-header"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0 0 4px",
        fontSize: SZ.micro,
        color: C.text.faint,
        letterSpacing: "0.3px",
      }}
    >
      <span style={{ width: 24, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>player</span>
      {keys.map((k) => (
        <span key={k} style={{ width: COLS[k].width, flexShrink: 0, textAlign: "right" }}>
          {COLS[k].label}
        </span>
      ))}
    </div>
  );
}

export const GRADE_COLOR = (grade) => {
  if (!grade) return C.text.faint;
  if (grade.startsWith("A")) return C.status.success;
  if (grade.startsWith("B")) return C.brand.accent;
  if (grade.startsWith("C")) return C.status.warning;
  return C.status.danger;
};

/**
 * The position signature this site already uses everywhere else: the position
 * word in its own POS_COLORS colour.
 *
 * 🔴 THIS REPLACED A LEFT COLOUR RAIL, DELIBERATELY. The card used to wear a
 * 4px grade-coloured stripe down its left edge, which is the default look of
 * every generated card on the internet and says nothing this page does not
 * already say twice. PlayerList, the boards and the trends charts all identify
 * a position by colouring the position TEXT, so the group title does that and
 * the card keeps the plain border every other card on /rate has.
 *
 * ONE COLOURED WORD, ONE BADGE. Grade colour lives in the badge and nowhere
 * else; position colour lives in the title and nowhere else. Two colour systems
 * on one card header is the most a reader can keep straight, and giving each of
 * them exactly one home is what makes the badge legible at a glance.
 *
 * `border` is the light-on-dark variant of each position colour and is the same
 * value theme.colors.pos carries for QB/RB/WR/TE; taking all of them off
 * POS_COLORS keeps FLEX, SFLEX, K and DEF on the same source rather than
 * splitting the lookup across two token sets.
 */
export function groupTitleColor(key) {
  return (POS_COLORS[key] && POS_COLORS[key].border) || C.text.primary;
}

export function surplusColor(pick) {
  if (pick.bench == null) return C.text.faint;
  if (pick.surplus > 0.5) return C.status.success;
  if (pick.surplus < -0.5) return C.status.danger;
  return C.text.secondary;
}

/**
 * One player line inside a group card.
 *
 * `proj` is the DISPLAY projection - the user's own points under the league's
 * own scoring. The badge above comes from somewhere else on purpose, on both
 * pages.
 */
export function GroupRow({ pick, proj, rank, wide, priceCols, hasPrices }) {
  const [open, setOpen] = useState(false);
  const still = prefersReducedMotion();
  // Two columns is the phone budget. The price detail is a tap away there and
  // inline on a wide viewport, so nothing is hidden, only deferred.
  const tappable = hasPrices && !wide;

  return (
    <div data-testid={`group-row-${pick.player}`} style={{ borderBottom: `1px solid ${C.border.default}` }}>
      <div
        onClick={tappable ? () => setOpen((v) => !v) : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 0",
          cursor: tappable ? "pointer" : "default",
        }}
      >
        <PlayerAvatar player={avatarPlayer(pick)} size={24} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: SZ.small,
              fontWeight: 700,
              color: C.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pick.player}
          </div>
          <div style={{ fontSize: SZ.micro, color: C.text.faint }}>
            {pick.pos}
            {pick.team ? ` ${pick.team}` : ""}
            {pick.lineupSlot && pick.lineupSlot !== pick.pos ? ` · ${pick.lineupSlot}` : ""}
          </div>
        </div>
        {/* PPG first: it is what the Projection Lab speaks and what a person
            can hold against a start/sit call. A player the projection set does
            not carry reads as a dash in both columns. It is not a zero and the
            grid must never let it look like one. */}
        <Cell col={COLS.ppg} color={proj ? C.text.primary : C.text.faint} weight={700}>
          {proj ? proj.ppg.toFixed(1) : NONE}
        </Cell>
        <Cell col={COLS.season} color={C.text.faint}>
          {proj ? proj.seasonPts.toFixed(0) : NONE}
        </Cell>
        <Cell col={COLS.rank} color={C.text.faint}>
          {rank ? `${pick.pos}${rank}` : NONE}
        </Cell>
        {priceCols && (
          <>
            {/* A pick nobody priced shows a dash, not a blank. Same rule as the
                market column below and for the same reason. */}
            <Cell col={COLS.paid} color={pick.paid == null ? C.text.faint : C.text.secondary}>
              {pick.paid == null ? NONE : money(pick.paid)}
            </Cell>
            <Cell col={COLS.market} color={C.text.faint}>
              {pick.bench == null ? NONE : money(pick.bench)}
            </Cell>
            <Cell col={COLS.delta} color={surplusColor(pick)} weight={600}>
              {pick.bench == null ? NONE : signedMoney(pick.surplus)}
            </Cell>
          </>
        )}
      </div>
      {tappable && open && (
        <div
          data-testid={`group-row-detail-${pick.player}`}
          style={{
            padding: "0 0 8px 32px",
            fontSize: SZ.micro,
            color: C.text.secondary,
            ...expandEnter(still),
          }}
        >
          {pick.paid == null ? "no price entered" : `paid ${money(pick.paid)}`}
          {pick.bench == null ? (
            " · too cheap for the market table, so it grades as even money"
          ) : (
            <>
              {" · market "}
              {money(pick.bench)}
              {" · "}
              <b style={{ color: surplusColor(pick) }}>{signedMoney(pick.surplus)}</b>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One position group: a badge, a POS-coloured title, a note, and its players.
 *
 * @param group     { key, kind, rows } — kind is "starters" or "bench"
 * @param badge     the big glyph or word. Null draws the dash.
 * @param badgeSub  the small line under it, or null for nothing.
 * @param badgeColor,badgeSize  the caller owns both; see the header for why.
 * @param note      the line under the title, in the caller's own words.
 * @param rowInfo   pick -> { proj, rank }, the two numbers the card cannot
 *                  derive because the two pages hold them in different objects.
 */
export default function GroupCard({
  group,
  badge,
  badgeSub,
  badgeColor,
  badgeSize,
  note,
  hasPrices,
  wide,
  rowInfo,
}) {
  // Decided ONCE per card, not per row. Deciding it per row let a card with one
  // unpriced pick draw six columns on some rows and three on others, under a
  // single header - the misalignment that made a lone "$1" unreadable.
  const priceCols = hasPrices && wide;
  return (
    <div
      data-testid={`group-card-${group.kind}-${group.key}`}
      style={{ ...card, padding: 14, marginBottom: 10 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div
          style={{
            width: 46,
            flexShrink: 0,
            textAlign: "center",
            borderRadius: theme.radius.lg,
            border: `1px solid ${C.border.default}`,
            background: C.bg.deep,
            padding: "4px 0",
          }}
        >
          <div
            data-testid={`group-badge-${group.kind}-${group.key}`}
            style={{
              fontSize: badgeSize || 18,
              fontWeight: 900,
              lineHeight: 1.1,
              color: badgeColor || C.text.faint,
            }}
          >
            {badge || NONE}
          </div>
          {badgeSub && (
            <div style={{ fontSize: 10, color: C.text.faint, lineHeight: 1.2 }}>{badgeSub}</div>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: SZ.body, fontWeight: 800, letterSpacing: "0.5px" }}>
            {/* The position word carries the colour; the rest of the title does
                not, so "RB bench" reads as a coloured RB and a plain noun. */}
            <span data-testid={`group-title-${group.kind}-${group.key}`} style={{ color: groupTitleColor(group.key) }}>
              {group.key}
            </span>
            {group.kind === "bench" ? " bench" : ""}
          </div>
          <div style={{ fontSize: SZ.micro, color: C.text.faint }}>{note}</div>
        </div>
      </div>
      {/* The header and every row take the same flag, so a column can never be
          named without being drawn or drawn without being named. */}
      <GroupHeader priceCols={priceCols} />
      {group.rows.map((pick, i) => {
        const info = (rowInfo && rowInfo(pick)) || {};
        return (
          <GroupRow
            key={`${pick.player}-${i}`}
            pick={pick}
            proj={info.proj || null}
            rank={info.rank || null}
            wide={wide}
            priceCols={priceCols}
            hasPrices={hasPrices}
          />
        );
      })}
    </div>
  );
}
